// @wc-ignore-file
// (Query keys are data, not copy — a translated key would split the cache.)
import { queryOptions } from "@tanstack/react-query";

import * as incognitoApi from "./incognito-api";

// Each fetch writes an audit row. Data stays cached for 30 minutes — including
// while nothing observes it (the route guard reads the workspace with
// ensureQueryData, which has no observer) — so navigation inside an incognito
// session does not log again; a new open after that window logs a new row.
const INCOGNITO_CACHE_TIME = 30 * 60 * 1000;

export const incognitoQueries = {
  root: () => ["superuser-incognito"] as const,
  workspaceBySlug: (slug: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "workspace", "slug", slug] as const,
      queryFn: () => incognitoApi.getIncognitoWorkspaceBySlug(slug),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  projects: (workspaceId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "workspace", workspaceId, "projects"] as const,
      queryFn: () => incognitoApi.listIncognitoWorkspaceProjects(workspaceId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  members: (workspaceId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "workspace", workspaceId, "members"] as const,
      queryFn: () => incognitoApi.listIncognitoWorkspaceMembers(workspaceId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  // CR-165 — keys under ["superuser-incognito", "project", …].
  projectBySlugs: (workspaceSlug: string, projectSlug: string) =>
    queryOptions({
      queryKey: [
        ...incognitoQueries.root(),
        "project",
        "slug",
        workspaceSlug,
        projectSlug,
      ] as const,
      queryFn: () => incognitoApi.getIncognitoProjectBySlugs(workspaceSlug, projectSlug),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  projectContainers: (projectId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "project", projectId, "containers"] as const,
      queryFn: () => incognitoApi.listIncognitoProjectContainers(projectId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  projectChangeRequests: (projectId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "project", projectId, "change-requests"] as const,
      queryFn: () => incognitoApi.listIncognitoProjectChangeRequests(projectId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  projectTasks: (projectId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "project", projectId, "tasks"] as const,
      queryFn: () => incognitoApi.listIncognitoProjectTasks(projectId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  projectMembers: (projectId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "project", projectId, "members"] as const,
      queryFn: () => incognitoApi.listIncognitoProjectMembers(projectId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  documentContent: (documentId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "document", documentId, "content"] as const,
      queryFn: () => incognitoApi.getIncognitoDocumentContent(documentId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  crEvents: (crId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "change-request", crId, "events"] as const,
      queryFn: () => incognitoApi.listIncognitoCrEvents(crId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  crDependencies: (crId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "change-request", crId, "dependencies"] as const,
      queryFn: () => incognitoApi.listIncognitoCrDependencies(crId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  crNotionReferences: (crId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "change-request", crId, "notion-references"] as const,
      queryFn: () => incognitoApi.listIncognitoCrNotionReferences(crId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  crJiraLinks: (crId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "change-request", crId, "jira-links"] as const,
      queryFn: () => incognitoApi.listIncognitoCrJiraLinks(crId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  crLinearLinks: (crId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "change-request", crId, "linear-links"] as const,
      queryFn: () => incognitoApi.listIncognitoCrLinearLinks(crId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
  roomMessages: (roomId: string) =>
    queryOptions({
      queryKey: [...incognitoQueries.root(), "room", roomId, "messages"] as const,
      queryFn: () => incognitoApi.listIncognitoRoomMessages(roomId),
      staleTime: INCOGNITO_CACHE_TIME,
      gcTime: INCOGNITO_CACHE_TIME,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    }),
};
